/**
 * Navigation Store
 *
 * Tracks recent pages and navigation history
 */

import { create } from 'zustand';

export interface RecentItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  timestamp: number;
}

interface NavigationStore {
  // State
  recentItems: RecentItem[];
  searchQuery: string;

  // Actions
  addRecentItem: (item: Omit<RecentItem, 'timestamp'>) => void;
  clearRecentItems: () => void;
  setSearchQuery: (query: string) => void;
}

const MAX_RECENT_ITEMS = 5;

const useNavigationStore = create<NavigationStore>((set, get) => ({
  // Initial state
  recentItems: [],
  searchQuery: '',

  // Add item to recent list
  addRecentItem: (item) => {
    const { recentItems } = get();

    // Don't add if it's already the most recent
    if (recentItems.length > 0 && recentItems[0].key === item.key) {
      return;
    }

    // Remove item if it already exists
    const filtered = recentItems.filter((i) => i.key !== item.key);

    // Add new item to beginning
    const newItems = [
      { ...item, timestamp: Date.now() },
      ...filtered,
    ].slice(0, MAX_RECENT_ITEMS);

    set({ recentItems: newItems });

    // Persist to localStorage (without icons - they contain circular refs)
    const itemsForStorage = newItems.map(({ key, label, timestamp }) => ({
      key,
      label,
      timestamp,
    }));
    localStorage.setItem('recent-items', JSON.stringify(itemsForStorage));
  },

  // Clear all recent items
  clearRecentItems: () => {
    set({ recentItems: [] });
    localStorage.removeItem('recent-items');
  },

  // Set search query
  setSearchQuery: (query) => {
    set({ searchQuery: query });
  },
}));

// Initialize from localStorage on first load
const stored = localStorage.getItem('recent-items');
if (stored) {
  try {
    const items = JSON.parse(stored);
    useNavigationStore.setState({ recentItems: items });
  } catch (e) {
    console.error('Failed to parse recent items from localStorage', e);
  }
}

export default useNavigationStore;
